export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
export const HAND_COUNT = 78;

/**
 * Reading order of the 78 non-pair hands in the lower triangle, column by column, top to bottom:
 *   AK AQ AJ … A2 (0–11), KQ KJ … K2 (12–22), QJ … Q2 (23–32), …, 32 (77).
 * Cell (row r, col c) with r > c holds hand RANKS[c] + RANKS[r].
 */
export function handIndex(row: number, col: number): number {
  if (!(row > col && row < 13 && col >= 0)) return -1;
  return 12 * col - (col * (col - 1)) / 2 + (row - col - 1);
}

/** Bare hand name for a lower-triangle cell, e.g. 'AK'. */
export function handLabel(row: number, col: number): string {
  return `${RANKS[col]}${RANKS[row]}`;
}

/** Which triangle(s) a term colours: offsuit = lower, suited = upper (its mirror), both = the two. */
export type Side = 'offsuit' | 'suited' | 'both';

/**
 * Full 13×13 cell: the lower triangle holds offsuit hands, the upper their suited mirrors (read row by row
 * left to right, which gives the same reading order), the diagonal pairs (never in play). Labels carry no
 * suit letter: 'AK' on both sides, 'AA' on the diagonal.
 */
export function cellAt(
  row: number,
  col: number,
): { label: string; index: number; side: Exclude<Side, 'both'> | 'pair' } {
  if (row === col) return { label: `${RANKS[row]}${RANKS[row]}`, index: -1, side: 'pair' };
  if (row > col) return { label: handLabel(row, col), index: handIndex(row, col), side: 'offsuit' };
  return { label: handLabel(col, row), index: handIndex(col, row), side: 'suited' };
}

/** Labels in reading order, e.g. HANDS[0] === 'AK', HANDS[77] === '32'. */
export const HANDS: readonly string[] = (() => {
  const out: string[] = new Array(HAND_COUNT);
  for (let r = 0; r < 13; r++) for (let c = 0; c < r; c++) out[handIndex(r, c)] = handLabel(r, c);
  return out;
})();

/**
 * The 12 hands holding rank `rank` (index into RANKS), in stripe order: along the row from the A end to the pairs
 * diagonal, then down the column to the 2 end. Rank 9: A9 K9 Q9 J9 T9, then 98 97 … 92. Rank A is the A column
 * (AK … A2), rank 2 the bottom row (A2 … 32).
 */
export function stripeOf(rank: number): number[] {
  const out: number[] = [];
  for (let c = 0; c < rank; c++) out.push(handIndex(rank, c));
  for (let r = rank + 1; r < 13; r++) out.push(handIndex(r, rank));
  return out;
}

/** Playable regions: which hand indices (reading order) a mode may colour. */
export const REGIONS = {
  /** all 78 hands */
  full: Array.from({ length: HAND_COUNT }, (_, i) => i),
  /** one rank's 12 hands (see `stripeOf`); the A column stands in when a term names no rank */
  stripe: stripeOf(0),
} as const satisfies Record<string, readonly number[]>;
export type Region = keyof typeof REGIONS;

/**
 * Lighting orders over the lower triangle (reading-order indices); the upper triangle mirrors them.
 *  - column:            from AK, column by column left to right, each column top to bottom (the reading order)
 *  - column reversed:   from 32, row by row bottom to top, each row right to left
 *  - diagonal:          diagonals parallel to the pairs, nearest first, each top to bottom (AK … 32, then AQ … 42, …)
 *  - diagonal reversed: the same diagonals, each bottom to top (32 … AK, then 42 … AQ, …)
 * The pairs diagonal is never part of any order.
 */
export type FillKind = 'column' | 'diagonal';
const ORDERS: Record<FillKind, readonly [readonly number[], readonly number[]]> = (() => {
  const colRev: number[] = [];
  for (let r = 12; r >= 1; r--) for (let c = r - 1; c >= 0; c--) colRev.push(handIndex(r, c));
  const diag: number[] = [];
  const diagRev: number[] = [];
  for (let d = 1; d <= 12; d++) {
    for (let c = 0; c + d < 13; c++) diag.push(handIndex(c + d, c));
    for (let c = 12 - d; c >= 0; c--) diagRev.push(handIndex(c + d, c));
  }
  return { column: [REGIONS.full, colRev], diagonal: [diag, diagRev] };
})();
export function fillOrder(kind: FillKind, reversed = false): readonly number[] {
  return ORDERS[kind][reversed ? 1 : 0];
}
